<script setup lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, useTemplateRef, watch } from 'vue';
import { useStepperStore } from '../../stores/stepper.store.js';
import { Tooltip, TooltipProvider } from '../Components/index.js';
import DateInput from './components/DateInput.vue';
import DeliveryAddress from './components/delivery-address.vue';
import Input from './components/Input.vue';
import TimeInput from './components/TimeInput.vue';
import { getTranslation } from './helpers/translations.helper.js';
import AddressAutoComplete from './modules/address-auto-complete/AddressAutoComplete.vue';
import type { AddressDetails } from './modules/address-auto-complete/address-auto-complete.types.js';

// TODO: Clean up this files.

defineComponent({
  components: {
    DeliveryAddress,
    Input,
    AddressAutoComplete,
    DateInput,
    TimeInput,
  },
});

const parentStepId = ref<string | null>(null);
const isFormSubmitted = ref<boolean>(false);
const selectedAddress = ref(useStepperStore().getState().deliveryAddress.deliveryAddressType);
const deliveryAddress = reactive(useStepperStore().getState().deliveryAddress);
const cardNote = ref('');

const isFuneral = computed(() => selectedAddress.value === 'funeralParlor');

const wrapper = useTemplateRef('wrapper');

function getTranslationSupportFuneral(key: string, isFuneral: boolean): string {
  if (isFuneral) {
    return getTranslation(key.split('_').join('_funeral_'));
  }
  return getTranslation(key);
}

onMounted(() => {
  parentStepId.value = wrapper.value?.closest('.product-config-step')?.id ?? '';
  useStepperStore()
    .getState()
    .blocks.registerBlock({
      blockId: 'order-form',
      stepId: parentStepId.value,
      enabled: true,
      validate: async () => {
        const errors: string[] = [];

        if (!deliveryAddress.firstName) {
          errors.push('First name is required');
        }

        if (!deliveryAddress.lastName) {
          errors.push('Last name is required');
        }

        if (selectedAddress.value !== 'home' && !deliveryAddress.company) {
          errors.push('Company is required for business and funeral parlor addresses');
        }

        if (!deliveryAddress.street) {
          errors.push('Street is required');
        }

        if (!deliveryAddress.houseNumber) {
          errors.push('House number is required');
        }

        if (!deliveryAddress.postalCode) {
          errors.push('Postal code is required');
        }

        if (!deliveryAddress.city) {
          errors.push('City is required');
        }

        if (isFuneral.value && !deliveryAddress.funeralDate) {
          errors.push('Funeral date is required for funeral parlor addresses');
        }

        if (isFuneral.value && !deliveryAddress.funeralTime) {
          errors.push('Funeral time is required for funeral parlor addresses');
        }

        if (errors.length > 0) {
          console.error('Validation errors:', errors);
        }

        return errors.length === 0;
      },
    });
});

function updateDeliveryAddressField(field: keyof typeof deliveryAddress, value: string | Date | undefined) {
  useStepperStore()
    .getState()
    .deliveryAddress.update({ [field]: value });
}

function updateDeliveryAddress(address: AddressDetails | undefined) {
  if (!address) {
    useStepperStore().getState().deliveryAddress.update({
      houseNumber: undefined,
      houseNumberAddition: undefined,
      street: undefined,
      city: undefined,
      postalCode: undefined,
    });
  } else {
    useStepperStore()
      .getState()
      .deliveryAddress.update({
        houseNumber: `${address.house_number}`,
        houseNumberAddition: address.extension ?? undefined,
        street: address.street,
        city: address.city,
        postalCode: address.postcode,
      });
  }
}

function updateCartNotes(value: string) {
  useStepperStore().getState().extras.setNote(value);
}

useStepperStore().subscribe((state) => {
  if (state.steps.steps.find((s) => s.stepId === parentStepId.value)?.isSubmitted) {
    isFormSubmitted.value = true;
  }

  // Update fields when possible
  deliveryAddress.firstName = state.deliveryAddress.firstName;
  deliveryAddress.lastName = state.deliveryAddress.lastName;
  deliveryAddress.company = state.deliveryAddress.company;
  deliveryAddress.street = state.deliveryAddress.street;
  deliveryAddress.houseNumber = state.deliveryAddress.houseNumber;
  deliveryAddress.houseNumberAddition = state.deliveryAddress.houseNumberAddition;
  deliveryAddress.postalCode = state.deliveryAddress.postalCode;
  deliveryAddress.city = state.deliveryAddress.city;
  deliveryAddress.phoneNumber = state.deliveryAddress.phoneNumber;
  deliveryAddress.funeralDate = state.deliveryAddress.funeralDate;
  deliveryAddress.funeralTime = state.deliveryAddress.funeralTime;
});

watch(deliveryAddress, () => {
  // Watch for changes in the zipcode here and make set the right shipping option.
  const localShippingOptions = document.querySelector<HTMLScriptElement>('#local-shipping-rules');

  if (localShippingOptions) {
    const shippingOptions: {
      default_product_id: string;
      local_product_id: string;
      local_zipcodes: string[];
    } = JSON.parse(localShippingOptions.innerHTML);

    // Get the first 4 digits of the zipcode
    const zipcodeNumbers = deliveryAddress.postalCode.slice(0, 4);

    if (shippingOptions.local_product_id && shippingOptions?.local_zipcodes?.includes(zipcodeNumbers)) {
      useStepperStore().getState().extras.setShipping(Number.parseInt(shippingOptions.local_product_id, 10));
    } else {
      useStepperStore().getState().extras.setShipping(Number.parseInt(shippingOptions.default_product_id, 10));
    }
  }
});
</script>

<template>
  <section ref="wrapper" :class="{ touched: isFormSubmitted }">
    <div class="order-form">
      <delivery-address v-model:delivery-address-type="selectedAddress" />
    </div>

    <div class="order-form order-form-2-cols">
      <Input :label="getTranslationSupportFuneral('label_firstname', isFuneral)"
        :placeholder="getTranslationSupportFuneral('placeholder_firstname', isFuneral)"
        :value="deliveryAddress.firstName" :required="true"
        @update:value="(value) => updateDeliveryAddressField('firstName', value)" />

      <Input :label="getTranslationSupportFuneral('label_lastname', isFuneral)"
        :placeholder="getTranslationSupportFuneral('placeholder_lastname', isFuneral)" :value="deliveryAddress.lastName"
        :required="true" @update:value="(value) => updateDeliveryAddressField('lastName', value)" />
    </div>

    <div class="order-form">
      <Input v-if="selectedAddress !== 'home'" :label="getTranslationSupportFuneral('label_company', isFuneral)"
        :placeholder="getTranslationSupportFuneral('placeholder_company', isFuneral)"
        :value="deliveryAddress.company ?? ''" :max-length="250" :required="true"
        @update:value="(value) => updateDeliveryAddressField('company', value)" />
    </div>

    <div class="order-form">
      <address-auto-complete :label="getTranslationSupportFuneral('label_address', isFuneral)"
        :placeholder="getTranslationSupportFuneral('placeholder_address', isFuneral)"
        :is-form-submitted="isFormSubmitted" :labels="{
          zipcode: getTranslationSupportFuneral('label_zipcode', isFuneral),
          house_number: getTranslationSupportFuneral('label_housenumber', isFuneral),
          extension: getTranslationSupportFuneral('label_extension', isFuneral),
          street: getTranslationSupportFuneral('label_address', isFuneral),
          city: getTranslationSupportFuneral('label_city', isFuneral),
          state: getTranslationSupportFuneral('label_state', isFuneral),
          country: getTranslationSupportFuneral('label_country', isFuneral),

          searchManual: 'Handmatig invoeren',
          noResults: 'Geen resultaten...',
          reset: 'Reset',
          searchError: 'Oeps! Er ging iets mis met zoeken, probeer het later nogmaals',
        }" :placeholders="{
          zipcode: getTranslationSupportFuneral('placeholder_zipcode', isFuneral),
          house_number: getTranslationSupportFuneral('placeholder_housenumber', isFuneral),
          extension: getTranslationSupportFuneral('placeholder_extension', isFuneral),
          street: getTranslationSupportFuneral('placeholder_address', isFuneral),
          city: getTranslationSupportFuneral('placeholder_city', isFuneral),
          state: getTranslationSupportFuneral('placeholder_state', isFuneral),
          country: getTranslationSupportFuneral('placeholder_country', isFuneral),
        }" @update:model-value="(address) => updateDeliveryAddress(address)" />
    </div>

    <div class="order-form">
      <Input :label="getTranslationSupportFuneral('label_phoneRecipient', isFuneral)"
        :placeholder="getTranslationSupportFuneral('placeholder_phoneRecipient', isFuneral)"
        :value="deliveryAddress.phoneNumber ?? ''" :max-length="30"
        @update:value="(value) => updateDeliveryAddressField('phoneNumber', value)" />
    </div>
    <div class="order-form order-form-2-cols" v-if="isFuneral">
      <date-input :label="getTranslation('label_funeralDate')" :placeholder="getTranslation('placeholder_funeralDate')"
        :model-value="deliveryAddress.funeralDate" :min-date="new Date()"
        @update:model-value="(value) => updateDeliveryAddressField('funeralDate', value)" />

      <time-input :label="getTranslation('label_funeralDateTime')"
        :placeholder="getTranslation('placeholder_funeralDateTime')" v-model="deliveryAddress.funeralTime"
        @update:model-value="(value) => updateDeliveryAddressField('funeralTime', value)" />
    </div>
    <y-input-wrapper>
      <label slot="label" class="is-centered" for="note">
        <span>{{ getTranslationSupportFuneral('label_notes', isFuneral) }}</span>
        <TooltipProvider>
          <Tooltip>
            {{ getTranslationSupportFuneral('tooltip_notes', isFuneral) }}
          </Tooltip>
        </TooltipProvider>
      </label>
      <textarea class="order-form__input-textarea" name="note"
        :placeholder="getTranslationSupportFuneral('placeholder_notes', isFuneral)" style="resize: vertical"
        v-model="cardNote" @update:model-value="(value: string) => updateCartNotes(value)"></textarea>
    </y-input-wrapper>
  </section>
</template>
